import React from 'react';
import { useReveal } from '../hooks/useReveal';

interface Job {
	title: string; company: string; location: string;
	from: string; to: string; type: string;
	bullets: string[]; tech: string[];
}

const jobs: Job[] = [
	{
		title: 'Full Stack Developer', company: 'Iknite Studio',
		location: 'Cameroon · Onsite', from: 'Nov 2023', to: 'Present', type: 'Full-time',
		bullets: [
			'Built a WhatsApp chatbot delivering real-time electricity outage alerts to 2,000+ users using microservices, AWS SNS/SQS, gRPC, Twilio, and an AI agent with ADK and MCP.',
			'Deployed Moodle & Nextcloud on AWS via CloudFormation with Redis, EFS, and CAS SSO for startups and SMEs.',
			'Cut AWS deployment costs by 50% by migrating infrastructure from Fargate to EC2.',
			'Reduced page load time by 20% by refactoring JS to TypeScript and optimizing UI components.',
			'Initiated modular backend architecture with PostgreSQL and GORM for scalability and team collaboration.',
		],
		tech: ['Go', 'gRPC', 'AWS', 'Next.js', 'TypeScript', 'PostgreSQL', 'DevOps'],
	},
	{
		title: 'Full Stack Developer', company: 'Crestlancing',
		location: 'Cameroon · Remote', from: 'Oct 2024', to: 'Jun 2025', type: 'Part-time',
		bullets: [
			'Developed Android user and driver apps for a ride & bus ticket booking platform with real-time tracking.',
			'Built a web dashboard for agency admins to manage tickets, fleets, and bookings.',
			'Designed and documented a scalable RESTful API integrating all platform clients.',
		],
		tech: ['TypeScript', 'Next.js', 'React Native', 'Golang', 'PostgreSQL', 'Tailwind CSS'],
	},
	{
		title: 'Backend Developer', company: 'Midatlantic Group',
		location: 'Cameroon · Remote', from: 'Jun 2024', to: 'Oct 2024', type: 'Contract',
		bullets: [
			'Led a team of 3 engineers to deliver a fintech app projected to serve 10,000+ users.',
			'Defined project scope, coordinated development, and ensured on-time delivery.',
			'Designed and deployed a robust backend API and DevOps infrastructure for secure, scalable delivery.',
		],
		tech: ['Node.js', 'Express', 'TypeScript', 'PostgreSQL', 'GitHub Actions'],
	},
	{
		title: 'Frontend Developer', company: 'Farway Technologies',
		location: 'India · Remote', from: 'Oct 2021', to: 'Dec 2021', type: 'Volunteer',
		bullets: [
			'Migrated Farway Security website from static HTML/CSS/JS to Next.js, reducing response time by 25%.',
			'Improved maintainability and UX with modular components, Tailwind CSS, and TypeScript.',
		],
		tech: ['Next.js', 'TypeScript', 'Tailwind CSS'],
	},
];

const JobCard = ({ job, index }: { job: Job; index: number }) => {
	const ref = useReveal();
	return (
		<div ref={ref} className={`reveal reveal-delay-${Math.min(index + 1, 4)} flex gap-6`}>
			{/* Timeline */}
			<div className="hidden sm:flex flex-col items-center gap-0 flex-shrink-0">
				<div className="w-3 h-3 rounded-full bg-accent shadow-glow mt-1.5 flex-shrink-0" />
				<div className="w-px flex-1 bg-gradient-to-b from-accent/50 to-transparent mt-1" />
			</div>

			<div className="section-card flex-1 mb-6">
				<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
					<div>
						<h3 className="font-display font-semibold text-white text-lg">{job.title}</h3>
						<p className="text-slate-400 text-sm mt-0.5">
							{job.company}
							<span className="text-slate-600 mx-1.5">·</span>
							{job.location}
						</p>
					</div>
					<div className="flex-shrink-0 text-right">
						<p className="text-sm text-slate-400 font-medium">{job.from} – {job.to}</p>
						<span className="inline-block mt-1 text-xs px-2.5 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/25 font-semibold">{job.type}</span>
					</div>
				</div>

				<ul className="space-y-2 mb-5">
					{job.bullets.map((b, j) => (
						<li key={j} className="flex gap-2.5 text-sm text-slate-400 leading-relaxed">
							<span className="text-accent mt-0.5 flex-shrink-0 text-xs">▸</span>
							{b}
						</li>
					))}
				</ul>

				<div className="flex flex-wrap gap-2 pt-4 border-t border-slate-700/50">
					{job.tech.map(t => (
						<span key={t} className="text-xs px-2.5 py-1 rounded-lg bg-slate-800 text-slate-400 font-medium border border-slate-700">{t}</span>
					))}
				</div>
			</div>
		</div>
	);
};

function WorkExperiences() {
	return (
		<section className="max-w-6xl mx-auto px-6">
			<div className="sm:pl-4">
				{jobs.map((job, i) => <JobCard key={i} job={job} index={i} />)}
			</div>
		</section>
	);
}

export default WorkExperiences;
